import { useEffect } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check, Mail } from 'lucide-react';
import { useAuth } from '../auth/useAuth';
import { useSubscriptions, useSetActiveSubscription, type Subscription } from '../features/subscriptions/api';
import { SubscriptionActionIcons } from '../features/subscriptions/SubscriptionActionIcons';
import { clipLabel, resolveSubscriptionLabel } from '../features/subscriptions/labels';

/**
 * /mailboxes/ — выбор, к ящикам какой подписки перейти.
 * У каждой строки две иконки: изменить название и открыть карточку.
 */
export function SubscriptionSelectPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, login } = useAuth();
  const subsQuery = useSubscriptions(isAuthenticated);
  const setActive = useSetActiveSubscription();
  const { deeplink } = useSearch({ strict: false });

  const items = subsQuery.data?.items ?? [];

  useEffect(() => {
    if (deeplink) return;
    if (subsQuery.isSuccess && items.length <= 1) {
      void navigate({ to: '/' });
    }
  }, [deeplink, subsQuery.isSuccess, items.length, navigate]);

  if (authLoading) {
    return <section className="page"><p>{t('common.loading')}</p></section>;
  }

  if (!isAuthenticated) {
    return (
      <section className="page">
        <p>{t('auth.notAuthenticated')}</p>
        <button type="button" className="mt-3 text-brand underline" onClick={() => { login('/mailboxes/'); }}>
          {t('auth.login')}
        </button>
      </section>
    );
  }

  if (subsQuery.isLoading) {
    return <section className="page"><p>{t('common.loading')}</p></section>;
  }

  if (subsQuery.isError) {
    return (
      <section className="page">
        <p className="text-danger">{t('subscriptions.loadError')}</p>
        <button type="button" className="mt-3 text-brand underline" onClick={() => { void subsQuery.refetch(); }}>
          {t('common.retry')}
        </button>
      </section>
    );
  }

  const choose = (id: string): void => {
    setActive.mutate(id, { onSuccess: () => { void navigate({ to: '/' }); } });
  };

  const openCard = (id: string, rename = false): void => {
    void navigate({
      to: '/subscriptions/$subscriptionId',
      params: { subscriptionId: id },
      search: rename ? { rename: true } : {},
    });
  };

  return (
    <section className="mx-auto max-w-lg">
      <h1 className="mb-1 text-2xl font-bold text-fg">{t('subscriptions.selectTitle')}</h1>
      <p className="mb-6 text-sm text-fg-muted">
        {items.length === 0 ? t('subscriptions.selectSubtitleEmpty') : t('subscriptions.selectSubtitle')}
      </p>

      {deeplink && (
        <p
          role="status"
          className="mb-6 flex items-start gap-2 rounded-lg border border-border bg-surface p-3 text-sm text-fg-muted"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-danger" aria-hidden="true" />
          <span>
            {deeplink === 'denied'
              ? t('subscriptions.deeplinkDenied')
              : t('subscriptions.deeplinkUnavailable')}
          </span>
        </p>
      )}

      {items.length === 0 && <p className="text-sm text-fg-muted">{t('subscriptions.empty')}</p>}

      <ul className="flex flex-col gap-3">
        {items.map((s, index) => {
          const full = resolveSubscriptionLabel(s.serviceLabel, index, (n) =>
            t('subscriptions.defaultName', { n }),
          );
          return (
            <li key={s.serviceSubscriptionId}>
              <div className="flex items-center gap-2 rounded-lg border border-border bg-surface p-2 pr-3">
                <button
                  type="button"
                  disabled={setActive.isPending}
                  onClick={() => { choose(s.serviceSubscriptionId); }}
                  className="flex min-w-0 flex-1 items-center gap-4 rounded-lg p-2 text-left transition hover:border-brand disabled:opacity-60"
                >
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-page text-brand">
                    <Mail className="h-5 w-5" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium text-fg" title={full}>
                      {clipLabel(full)}
                    </span>
                    <span className="block truncate text-xs text-fg-muted">
                      {s.servicePlan ? `${s.servicePlan} · ` : ''}{statusLabel(t, s)}
                    </span>
                  </span>
                  {setActive.isPending && setActive.variables === s.serviceSubscriptionId ? (
                    <span className="text-xs text-fg-muted">{t('common.loading')}</span>
                  ) : (
                    <Check className="h-5 w-5 shrink-0 text-transparent" />
                  )}
                </button>
                <SubscriptionActionIcons
                  onRename={() => {
                    openCard(s.serviceSubscriptionId, true);
                  }}
                  onOpenCard={() => {
                    openCard(s.serviceSubscriptionId);
                  }}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function statusLabel(t: (k: string) => string, s: Subscription): string {
  const key = `subscriptions.status.${s.status}`;
  const label = t(key);
  return label === key ? s.status : label;
}
